(() => {
  if (window.NurtureWisePdpModules) {
    return;
  }

  const controllerByRoot = new WeakMap();
  let lastScrollY = window.scrollY;
  let frameRequested = false;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initImageAlts = (root, signal) => {
    const media = window.matchMedia('(max-width: 749px)');
    root.querySelectorAll('[data-nw-responsive-alt]').forEach((wrapper) => {
      const img = wrapper.querySelector('img');
      if (!img) return;
      const update = () => { img.alt = media.matches ? wrapper.dataset.mobileAlt : wrapper.dataset.desktopAlt; };
      media.addEventListener('change', update, { signal });
      update();
    });
  };

  const initDescriptions = (root, signal) => {
    root.querySelectorAll('[data-nw-description]').forEach((component) => {
      if (component.dataset.nwReady === 'true') return;
      component.dataset.nwReady = 'true';

      const trigger = component.querySelector('[data-nw-description-trigger]');
      const panel = component.querySelector('[data-nw-description-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', () => {
        const shouldOpen = trigger.getAttribute('aria-expanded') !== 'true';
        trigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        panel.hidden = !shouldOpen;
      }, { signal });
    });
  };

  const initAccordions = (root, signal) => {
    root.querySelectorAll('[data-nw-image-accordion], [data-nw-key-features]').forEach((component) => {
      if (component.dataset.nwReady === 'true') return;
      component.dataset.nwReady = 'true';
      const singleOpen = component.dataset.singleOpen === 'true';

      const states = new Map();
      const setOpen = (item, open) => {
        const summary = item.querySelector('summary');
        const previous = states.get(item);
        const from = item.getBoundingClientRect().height;
        if (previous?.animation) previous.animation.cancel();
        const state = { open, animation: null };
        states.set(item, state);
        summary.setAttribute('aria-expanded', String(open));
        const finish = () => {
          if (states.get(item) !== state) return;
          item.open = open;
          item.style.removeProperty('overflow');
          states.delete(item);
        };
        if (reduceMotion() || !item.animate) { finish(); return; }
        item.open = true;
        const to = open ? item.getBoundingClientRect().height : summary.getBoundingClientRect().height;
        item.style.overflow = 'hidden';
        const animation = item.animate([{ height: from + 'px' }, { height: to + 'px' }], { duration: 220, easing: 'ease' });
        state.animation = animation;
        animation.onfinish = finish;
      };
      let initialOpenFound = false;
      component.querySelectorAll('details').forEach((item, index) => {
        const summary = item.querySelector('summary');
        if (!summary) return;
        if (singleOpen && item.open) {
          if (initialOpenFound) item.open = false;
          initialOpenFound = true;
        }
        const panel = item.querySelector('.nw-kf-row__panel');
        if (panel) {
          panel.id = panel.id || `${component.id}-panel-${index}`;
          summary.setAttribute('aria-controls', panel.id);
        }
        summary.setAttribute('aria-expanded', String(item.open));
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          const open = !(states.get(item)?.open ?? item.open);
          if (singleOpen && open) component.querySelectorAll('details').forEach((other) => {
            if (other !== item && (states.get(other)?.open ?? other.open)) setOpen(other, false);
          });
          setOpen(item, open);
        }, { signal });
        item.addEventListener('toggle', () => {
          if (states.has(item)) return;
          summary.setAttribute('aria-expanded', String(item.open));
          if (!singleOpen || !item.open) return;
          component.querySelectorAll('details[open]').forEach((openItem) => {
            if (openItem !== item) openItem.open = false;
          });
        }, { signal });
      });
      signal.addEventListener('abort', () => states.forEach((state, item) => {
        state.animation?.cancel();
        item.open = state.open;
        item.style.removeProperty('overflow');
      }), { once: true });
    });
  };

  const initCards = (root, signal) => {
    root.querySelectorAll('[data-nw-cards-track]').forEach((track) => {
      let drag = null;
      let suppressClick = false;
      const scrollTo = (left) => track.scrollTo({ left, behavior: reduceMotion() ? 'auto' : 'smooth' });
      track.addEventListener('keydown', (event) => {
        if (event.target !== track) return;
        const step = (track.firstElementChild?.getBoundingClientRect().width || 0) + parseFloat(getComputedStyle(track).gap || 0);
        const destinations = { ArrowLeft: track.scrollLeft - step, ArrowRight: track.scrollLeft + step, Home: 0, End: track.scrollWidth };
        if (!(event.key in destinations)) return;
        event.preventDefault();
        scrollTo(destinations[event.key]);
      }, { signal });
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
        suppressClick = false;
      }, { signal });
      track.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const delta = event.clientX - drag.x;
        if (!drag.moved && Math.abs(delta) < 6) return;
        drag.moved = true;
        track.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
        track.scrollLeft = drag.left - delta;
      }, { signal });
      const endDrag = () => {
        if (!drag) return;
        suppressClick = drag.moved;
        drag = null;
        track.classList.remove('is-dragging');
      };
      track.addEventListener('pointerup', endDrag, { signal });
      track.addEventListener('pointercancel', endDrag, { signal });
      track.addEventListener('lostpointercapture', endDrag, { signal });
      track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      track.addEventListener('click', (event) => {
        if (suppressClick) { event.preventDefault(); suppressClick = false; }
      }, { signal, capture: true });
    });
  };

  const targetFromLink = (link) => {
    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return null;
    }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) return null;
    try {
      return document.getElementById(decodeURIComponent(url.hash.slice(1)));
    } catch (error) {
      return null;
    }
  };

  const initAnchorNav = (root, signal) => {
    root.querySelectorAll('[data-nw-product-anchor-nav]').forEach((nav) => {
      if (nav.dataset.nwReady === 'true') return;
      nav.dataset.nwReady = 'true';

      const wrapper = nav.closest('.shopify-section');
      const header = document.querySelector('.site-header');
      const headerWrapper = header && header.closest('.shopify-section');
      const list = nav.querySelector('.nw-product-anchor-nav__list');
      const links = Array.from(nav.querySelectorAll('[data-nw-anchor-link]'));
      if (wrapper) wrapper.classList.add('nw-product-anchor-nav-section');

      const revealLink = (link) => {
        if (!list || list.scrollWidth <= list.clientWidth) return;
        const listRect = list.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        if (linkRect.left < listRect.left) list.scrollLeft += linkRect.left - listRect.left;
        else if (linkRect.right > listRect.right) list.scrollLeft += linkRect.right - listRect.right;
      };

      const updateMetrics = () => {
        const navHeight = Math.ceil(nav.getBoundingClientRect().height || 54);
        document.documentElement.style.setProperty('--nw-anchor-height', `${navHeight}px`);
        const headerVisible = header && !header.classList.contains('nw-header-hidden-by-anchor');
        const headerBottom = headerVisible ? Math.max(0, Math.ceil((headerWrapper || header).getBoundingClientRect().bottom + 8)) : 0;
        document.documentElement.style.setProperty('--nw-anchor-offset', `${headerBottom + navHeight + 16}px`);
        if (wrapper) wrapper.style.setProperty('--nw-anchor-sticky-top', `${headerBottom}px`);
      };

      const setActive = () => {
        const targets = links.map((link) => targetFromLink(link));
        const offset = parseFloat(document.documentElement.style.getPropertyValue('--nw-anchor-offset')) + 16;
        let activeIndex = -1;
        targets.forEach((target, index) => {
          if (target && target.getBoundingClientRect().top <= offset) activeIndex = index;
        });
        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
          for (let index = targets.length - 1; index >= 0; index -= 1) {
            if (targets[index]) {
              activeIndex = index;
              break;
            }
          }
        } else if (activeIndex < 0) {
          activeIndex = targets.findIndex(Boolean);
        }

        links.forEach((link, index) => {
          const isActive = index === activeIndex;
          link.classList.toggle('is-active', isActive);
          if (isActive) {
            link.setAttribute('aria-current', 'location');
          } else {
            link.removeAttribute('aria-current');
          }
          if (isActive && link.dataset.nwWasActive !== 'true') {
            revealLink(link);
          }
          link.dataset.nwWasActive = isActive ? 'true' : 'false';
        });
      };

      const updateScrollState = () => {
        frameRequested = false;
        const currentY = Math.max(0, window.scrollY);
        const movingDown = currentY > lastScrollY + 4;
        const movingUp = currentY < lastScrollY - 4;

        if (header && nav.dataset.hideHeader === 'true' && currentY > 180) {
          if (movingDown) header.classList.add('nw-header-hidden-by-anchor');
          if (movingUp) header.classList.remove('nw-header-hidden-by-anchor');
        } else if (header) {
          header.classList.remove('nw-header-hidden-by-anchor');
        }

        lastScrollY = currentY;
        updateMetrics();
        setActive();
      };

      const requestUpdate = () => {
        if (frameRequested) return;
        frameRequested = true;
        window.requestAnimationFrame(updateScrollState);
      };

      links.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const target = targetFromLink(link);
          if (!target) return;
          if (header && nav.dataset.hideHeader === 'true') {
            const targetTop = target.getBoundingClientRect().top;
            header.classList.toggle('nw-header-hidden-by-anchor', targetTop > 0 && window.scrollY + targetTop > 180);
          }
          updateMetrics();
          target.style.scrollMarginTop = 'var(--nw-anchor-offset)';
          revealLink(link);
        }, { signal });
      });

      window.addEventListener('scroll', requestUpdate, { passive: true, signal });
      window.addEventListener('resize', requestUpdate, { passive: true, signal });
      window.addEventListener('orientationchange', requestUpdate, { passive: true, signal });
      window.addEventListener('popstate', requestUpdate, { passive: true, signal });
      window.addEventListener('hashchange', requestUpdate, { passive: true, signal });
      updateScrollState();
    });
  };

  const boot = (root = document) => {
    if (!root || !root.querySelectorAll) return;
    if (controllerByRoot.has(root)) return;
    const controller = new AbortController();
    controllerByRoot.set(root, controller);
    initImageAlts(root, controller.signal);
    initDescriptions(root, controller.signal);
    initAccordions(root, controller.signal);
    initCards(root, controller.signal);
    initAnchorNav(root, controller.signal);
  };

  const unload = (root) => {
    const controller = controllerByRoot.get(root);
    if (controller) controller.abort();
    controllerByRoot.delete(root);
    root.querySelectorAll('[data-nw-ready]').forEach((component) => delete component.dataset.nwReady);
    const wrapper = root.closest && root.closest('.shopify-section');
    if (wrapper) wrapper.classList.remove('nw-product-anchor-nav-section');
  };

  window.NurtureWisePdpModules = { boot, unload };
  document.querySelectorAll('.shopify-section').forEach((section) => boot(section));
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
